import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon, Divider
} from "@chakra-ui/react";
import { FaTint, FaArrowRight, FaCheckCircle, FaExclamationTriangle, FaQuoteLeft } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA = "#2C7DA0";
const ICE_MIST = "#EAF3FB";

const WaterBlogTapWaterSafe: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water/blog/tap-water-safe-australia"
        fallbackTitle="Is Tap Water Safe to Drink in Australia? — One Water Australia"
        fallbackDescription="The honest, balanced answer. Australian tap water meets national guidelines — but 'compliant' and 'safe' mean different things, and the gaps are real."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box position="relative" bg={NAVY} color="white" py={{ base: 14, md: 20 }} overflow="hidden">
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgImage="url('https://images.unsplash.com/photo-1504472478235-9bc48ba4d60f?auto=format&fit=crop&w=1920&q=80')" bgSize="cover" bgPosition="center" zIndex={0} />
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgGradient={`linear(to-br, ${NAVY}E8, ${DEEP_AQUA}AA)`} zIndex={0} />
        <Container maxW="container.md" position="relative" zIndex={1}>
          <VStack spacing={5} textAlign="center">
            <Text fontSize="sm" fontWeight="700" color={WATER_BLUE} textTransform="uppercase" letterSpacing="wider">
              Pillar Article
            </Text>
            <Heading size="2xl" fontWeight="bold" letterSpacing="-0.02em">
              Is Tap Water Safe to Drink in Australia?
            </Heading>
            <Text fontSize="lg" color="whiteAlpha.800" maxW="2xl">
              The answer most sources give you is too simple. Here is a more complete one.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Featured image */}
      <Box h={{ base: "240px", md: "420px" }} overflow="hidden">
        <Box
          as="img"
          src="https://images.unsplash.com/photo-1504472478235-9bc48ba4d60f?auto=format&fit=crop&w=1200&q=80"
          alt="Clean drinking water from an Australian kitchen tap"
          w="full"
          h="full"
          objectFit="cover"
          objectPosition="center"
        />
      </Box>

      <Container maxW="container.md" py={16}>
        <VStack spacing={14} align="stretch">

          {/* The short answer — qualified */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>The Short Answer — With Necessary Qualifications</Heading>
            <Text color="gray.700" mb={5} lineHeight="tall" fontSize="lg">
              By the standards of the Australian Drinking Water Guidelines (ADWG), yes — Australian tap water is generally compliant and considered safe for consumption. Waterborne disease rates are very low. The treatment infrastructure is well-funded by international comparison.
            </Text>
            <Text color="gray.700" mb={5} lineHeight="tall">
              That is the accurate starting point. It is also, on its own, insufficient.
            </Text>
            <Text color="gray.700" mb={5} lineHeight="tall">
              "Compliant with guidelines" and "safe" are not identical concepts. Guidelines set thresholds based on the best available science at the time of writing, tolerable dose estimates with built-in safety margins, and political and economic feasibility. They are not a guarantee that every substance in your tap water has been adequately studied, that your specific supply matches the distribution average, or that long-term cumulative exposure at currently "acceptable" levels carries no meaningful risk.
            </Text>
            <Box p={5} bg={ICE_MIST} borderRadius="xl" border="1px solid" borderColor={`${WATER_BLUE}40`}>
              <HStack spacing={3} mb={2} align="flex-start">
                <Icon as={FaExclamationTriangle} color={DEEP_AQUA} mt={0.5} flexShrink={0} />
                <Text fontSize="sm" color="gray.700" lineHeight="tall">
                  <Text as="span" fontWeight="700">An important structural point:</Text> The ADWG are guidelines — not legally enforceable national standards. Compliance is assessed and reported by the same utilities that operate the systems. Independent, third-party testing at the tap is the only way to know with confidence what you are actually drinking.
                </Text>
              </HStack>
            </Box>
          </Box>

          <Divider borderColor="gray.200" />

          {/* How it's regulated — honest version */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>How Australian Tap Water Is Regulated — and What That Means in Practice</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              Understanding the regulatory structure is important for understanding how much weight to give utility assurances.
            </Text>
            <VStack spacing={4} align="stretch">
              {[
                {
                  title: "The ADWG are guidelines, not law",
                  desc: "The Australian Drinking Water Guidelines are produced by the NHMRC and cover over 250 parameters. They are the benchmark for water quality assessment in Australia. They are not, however, legally enforceable national standards. Each state and territory has its own legislative framework for water quality — and the degree of independent enforcement varies significantly.",
                },
                {
                  title: "Utilities report on their own compliance",
                  desc: "Annual drinking water quality reports are produced by the utilities themselves. While independent auditing does occur, the primary compliance reporting mechanism is self-assessment. This is a structural conflict of interest — not necessarily bad faith, but a reality that matters when evaluating the assurance being offered.",
                },
                {
                  title: "Monitoring occurs at treatment plants and network points — not at your tap",
                  desc: "Water quality data in annual reports reflects what leaves treatment facilities and is measured at distribution monitoring points. It does not represent what comes out of your kitchen tap. Contamination events between those monitoring points and your glass — from ageing service pipes, internal plumbing, hot water systems, or roof tanks — are invisible to this reporting system.",
                },
                {
                  title: "Not all contaminants have guidelines",
                  desc: "PFAS compounds, microplastics, pharmaceutical residues, and many emerging contaminants do not have ADWG guideline values. Their absence from the guideline does not mean their absence from the water — it means they have not been formally assessed and included. Utilities have no obligation to test for what is not on the list.",
                },
              ].map((item) => (
                <Box key={item.title} p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                  <Text fontWeight="700" color={NAVY} mb={2} fontSize="sm">{item.title}</Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.desc}</Text>
                </Box>
              ))}
            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* What's in it — honest concern levels */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>What's Actually in Australian Tap Water</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              The following is a more candid assessment of the major components than you will typically find in utility communications.
            </Text>
            <VStack spacing={5} align="stretch">

              <Box p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                  <Text fontWeight="700" color={NAVY} fontSize="sm">Chlorine and disinfection byproducts (DBPs)</Text>
                  <Text fontSize="xs" fontWeight="600" px={3} py={1} borderRadius="full" bg={ICE_MIST} color={DEEP_AQUA}>
                    Established concern — research ongoing
                  </Text>
                </HStack>
                <Text fontSize="xs" color={DEEP_AQUA} fontWeight="500" mb={3}>0.1–1.0 mg/L free chlorine typical; DBP levels vary by source water and season</Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={3}>
                  Chlorine is added for disinfection and its role in preventing waterborne disease is well-established and important. The issue is not chlorine itself at the tap — it is what chlorine produces when it reacts with naturally occurring organic matter in the distribution system: trihalomethanes (THMs) and haloacetic acids (HAAs), collectively known as disinfection byproducts.
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={3}>
                  The International Agency for Research on Cancer classifies chloroform (a common THM) as a Group 2A probable human carcinogen. Multiple large epidemiological studies — including a multi-country European study involving over 400,000 participants — have found associations between long-term consumption of chlorinated drinking water and elevated risk of bladder cancer and adverse reproductive outcomes. These are peer-reviewed findings in major journals, not fringe claims.
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  Where utilities have switched from chlorine to chloramine — which produces a different and in some respects more complex DBP profile, including NDMA (a potent carcinogen at elevated levels) — communities have reported changes in taste, skin sensitivity, and digestive symptoms that authorities typically attribute to coincidence. The research literature does not conclusively support the dismissal.
                </Text>
              </Box>

              <Box p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                  <Text fontWeight="700" color={NAVY} fontSize="sm">Fluoride</Text>
                  <Text fontSize="xs" fontWeight="600" px={3} py={1} borderRadius="full" bg={ICE_MIST} color={DEEP_AQUA}>
                    Genuinely contested — emerging neurodevelopment research
                  </Text>
                </HStack>
                <Text fontSize="xs" color={DEEP_AQUA} fontWeight="500" mb={3}>0.6–1.0 mg/L in most fluoridated Australian supplies</Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={3}>
                  Fluoride's dental health benefit at community doses is well-supported by research, and Australian health authorities are not wrong to reference this. However, describing fluoride concern as simply a "personal preference" — as much official communication does — misrepresents the current state of the scientific literature.
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={3}>
                  In 2020, a peer-reviewed study in <Text as="span" fontStyle="italic">JAMA Pediatrics</Text> (Green et al.) found a statistically significant association between higher maternal urinary fluoride during pregnancy and lower IQ scores in Canadian children — at fluoride exposure levels consistent with fluoridated water consumption. The study faced significant institutional criticism, much of it from public health bodies with established positions on fluoridation.
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={3}>
                  In 2024, the US National Toxicology Program — a federal scientific body independent of water policy advocacy — completed a systematic review of fluoride and neurodevelopment. Their conclusion: <Text as="span" fontWeight="600" color={NAVY}>"moderate confidence" that fluoride is associated with lower IQ in children</Text>, with significant uncertainty remaining at doses between 0.5 and 1.5 mg/L — which includes the Australian target range. A US federal court subsequently ruled that the EPA must take regulatory action on fluoride as a potential neurodevelopmental risk.
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  This does not settle the question. What it does establish is that people who choose to filter fluoride from their drinking water — particularly households with young children or pregnant women — are acting on legitimate scientific concern, not irrationality. Only reverse osmosis reliably removes fluoride.
                </Text>
              </Box>

              <Box p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                  <Text fontWeight="700" color={NAVY} fontSize="sm">PFAS (per- and polyfluoroalkyl substances)</Text>
                  <Text fontSize="xs" fontWeight="600" px={3} py={1} borderRadius="full" bg={ICE_MIST} color={NAVY}>
                    Documented Australian contamination — not in standard reporting
                  </Text>
                </HStack>
                <Text fontSize="xs" color={DEEP_AQUA} fontWeight="500" mb={3}>Not routinely monitored by Australian water utilities — no ADWG guideline for most PFAS compounds</Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={3}>
                  PFAS are synthetic chemicals used in firefighting foam, cookware coatings, food packaging, and industrial processes. They do not break down in the environment or in the human body — hence "forever chemicals." They have been detected in the blood of virtually all Australians tested, in human breast milk, placental tissue, and in drinking water sources near dozens of Australian sites.
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={3}>
                  Documented Australian contamination sites include Williamtown (NSW), Oakey (QLD), Katherine (NT), RAAF Tindal, Edinburgh (SA), and many others — primarily near defence bases that used PFAS-containing firefighting foam for decades. Communities near these sites have faced restrictions on bore water and local produce use for years. Some residents reported health concerns for years before contamination was officially acknowledged.
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  The IARC classified PFOA (one common PFAS) as a Group 1 human carcinogen in 2023 — the highest classification, meaning sufficient evidence of cancer causation in humans. PFAS are not part of standard Australian utility annual water quality reporting. Seqwater, Sydney Water, SA Water, and other major utilities do not routinely test for the full suite of PFAS compounds. You cannot determine your PFAS exposure from an annual drinking water quality report.
                </Text>
              </Box>

              <Box p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                  <Text fontWeight="700" color={NAVY} fontSize="sm">Microplastics</Text>
                  <Text fontSize="xs" fontWeight="600" px={3} py={1} borderRadius="full" bg={ICE_MIST} color={DEEP_AQUA}>
                    Detected everywhere — no Australian guideline or monitoring obligation
                  </Text>
                </HStack>
                <Text fontSize="xs" color={DEEP_AQUA} fontWeight="500" mb={3}>Detected in treated Australian tap water; no ADWG guideline value exists</Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={3}>
                  Microplastics have been found in Australian tap water, bottled water, rainwater, and remote environments. They have been detected in human blood, lung tissue, intestinal tissue, placental tissue, and breast milk. A 2022 peer-reviewed study in <Text as="span" fontStyle="italic">Environment International</Text> (Leslie et al.) confirmed microplastics in the bloodstream of 77 of 22 healthy adult donors.
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  The WHO's 2022 microplastics review concluded there is currently insufficient evidence to determine the level of human health risk, while explicitly recommending precautionary action and urgent improvements in monitoring. There is no ADWG guideline for microplastics — meaning utilities are not required to test for them, report on them, or take any action regarding them. The absence of a guideline should not be read as a determination of safety; it reflects the absence of a completed regulatory process.
                </Text>
              </Box>

              <Box p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                  <Text fontWeight="700" color={NAVY} fontSize="sm">Lead and heavy metals</Text>
                  <Text fontSize="xs" fontWeight="600" px={3} py={1} borderRadius="full" bg={ICE_MIST} color={NAVY}>
                    No safe level in children — infrastructure-dependent
                  </Text>
                </HStack>
                <Text fontSize="xs" color={DEEP_AQUA} fontWeight="500" mb={3}>Mains water may be within guideline; individual taps may not be — particularly in pre-1989 buildings</Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={3}>
                  The WHO and the US CDC have both stated that there is no safe blood lead level for children. Lead is a neurotoxin with no lower threshold for cognitive harm in developing brains. The ADWG guideline for lead in drinking water is 0.01 mg/L — which is 10 micrograms per litre. Some toxicologists argue this remains too permissive given what is now known about sub-threshold neurological effects.
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  Properties built before approximately 1989 may contain lead solder in internal copper plumbing or lead service connections from the street main. Mains water may be clean and within guidelines at the distribution point; lead can be leached from internal plumbing between the meter and the tap. Testing in older Melbourne suburbs in 2019 identified tap-level lead exceedances in properties where mains compliance was unquestioned. This pattern — clean mains, contaminated tap — is the same mechanism that produced the Flint, Michigan crisis at larger scale.
                </Text>
              </Box>

              <Box p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                  <Text fontWeight="700" color={NAVY} fontSize="sm">Trace pharmaceuticals and agricultural chemicals</Text>
                  <Text fontSize="xs" fontWeight="600" px={3} py={1} borderRadius="full" bg="gray.100" color="gray.600">
                    Detected — health significance at trace levels uncertain
                  </Text>
                </HStack>
                <Text fontSize="xs" color={DEEP_AQUA} fontWeight="500" mb={3}>Typically at parts-per-trillion levels; long-term cumulative effects not adequately studied</Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  Traces of pharmaceuticals — including hormones, antibiotics, anti-depressants, and pain medications — have been detected in Australian source water and in some treated supplies. Agricultural chemicals including herbicides and pesticides are detectable seasonally in surface water catchments. Concentrations are typically orders of magnitude below any dose associated with acute harm. What is genuinely unknown is the cumulative effect of decades of very low-level exposure to a mixture of these compounds — an area the existing guideline structure is not designed to evaluate.
                </Text>
              </Box>

            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Community evidence */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>Community Evidence: Why Dismissal Is Not the Same as Disproof</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              A consistent pattern plays out when communities raise water quality concerns: symptoms are reported, utilities cite guideline compliance, the connection is officially denied, and the conversation effectively ends. In some cases — Williamtown being the clearest Australian example — the community was eventually proven correct.
            </Text>
            <VStack spacing={5} align="stretch">
              <Box p={5} borderLeft={`3px solid ${WATER_BLUE}`} bg={ICE_MIST} borderRadius="sm" borderRightRadius="xl">
                <Text fontWeight="700" color={NAVY} mb={2} fontSize="sm">The Williamtown pattern</Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  Residents near RAAF Base Williamtown in the Hunter Valley raised health concerns and noticed water issues for years. They were reassured that their water met guidelines. PFAS contamination from the base's firefighting foam was eventually confirmed at levels that triggered public health advisories, property purchase buyouts, and a class action. The community evidence preceded the institutional acknowledgement by years.
                </Text>
              </Box>
              <Box p={5} borderLeft={`3px solid ${WATER_BLUE}`} bg={ICE_MIST} borderRadius="sm" borderRightRadius="xl">
                <Text fontWeight="700" color={NAVY} mb={2} fontSize="sm">Chloramine switching and reported community health changes</Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  Several Australian water authorities have switched from chlorine to chloramine disinfection over the past decade. Following such switches, communities in affected areas have reported increases in skin irritation, gastrointestinal complaints, and changes in taste and odour. The standard utility response has been to attribute these reports to coincidence or heightened awareness. The US experience — particularly following Washington DC's chloramine switch in 2000, which was subsequently linked to elevated lead leaching — suggests the dismissal of community reports as a first response warrants more scrutiny than it typically receives.
                </Text>
              </Box>
              <Box p={5} borderLeft={`3px solid ${WATER_BLUE}`} bg={ICE_MIST} borderRadius="sm" borderRightRadius="xl">
                <Text fontWeight="700" color={NAVY} mb={2} fontSize="sm">Post-infrastructure works taste and colour changes</Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  Community forums, particularly in older suburban areas, regularly document reports of discolouration, unusual taste, and health symptoms following local pipe works, main replacements, or pressure changes. These reports are consistently met with utility assurances of guideline compliance. What is rarely communicated is that infrastructure disturbance events can temporarily mobilise accumulated scale deposits — containing manganese, iron, and sometimes heavy metals — that were stable before the works. Short-duration spikes in contaminants at individual taps may not be captured by routine monitoring.
                </Text>
              </Box>
            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* The gaps */}
          <Box>
            <Heading size="lg" color={NAVY} mb={2}>Where the System's Gaps Are Structural, Not Accidental</Heading>
            <Text color="gray.600" mb={6} fontSize="sm">These are not failures of the individuals running the system — they are design limitations worth understanding.</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {[
                {
                  gap: "Monitoring stops before your tap",
                  detail: "Utility testing occurs at treatment plants and network monitoring points — typically not at individual residential taps. What happens in the last hundred metres of water's journey through your service connection, internal pipes, and hot water system is outside the scope of annual reporting.",
                },
                {
                  gap: "Self-reporting structure",
                  detail: "Annual water quality reports are produced by utilities. Compliance is a self-assessment. Independent auditing supplements but does not replace this. The entity with commercial and reputational interest in the outcome is primarily responsible for determining whether the outcome meets the standard.",
                },
                {
                  gap: "Guidelines lag behind science",
                  detail: "ADWG updates take years. Emerging contaminants like PFAS, microplastics, and pharmaceutical residues are reviewed on timelines that do not keep pace with detection capabilities or accumulating epidemiological evidence. Absence of a guideline value reflects the pace of regulatory process — not a determination of safety.",
                },
                {
                  gap: "No ADWG testing requirement for unscheduled contaminants",
                  detail: "Utilities test for what the guidelines require. If a substance doesn't have a guideline value, there is no regulatory driver to test for it. The result is a systematic blind spot for emerging contaminants — which are, by definition, those most likely to have been overlooked.",
                },
                {
                  gap: "Averages conceal variation",
                  detail: "Reported figures are spatial and temporal averages across supply zones. Individual properties — particularly those distant from treatment plants, in older infrastructure corridors, or at the ends of distribution branches — may consistently experience water quality that differs meaningfully from the reported zone average.",
                },
                {
                  gap: "Property plumbing is your responsibility",
                  detail: "The utility's obligation ends at the boundary of your property. Internal plumbing, hot water systems, irrigation lines, and roof tanks are outside their scope. Pre-1989 plumbing materials that introduce lead or copper at levels exceeding guidelines are not a utility problem — they are a homeowner problem that the reporting system cannot detect.",
                },
              ].map((item) => (
                <Box key={item.gap} p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                  <Text fontWeight="700" color={NAVY} mb={2} fontSize="sm">{item.gap}</Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.detail}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.200" />

          {/* What you can do */}
          <Box>
            <Heading size="lg" color={NAVY} mb={2}>What You Can Do</Heading>
            <Text color="gray.600" mb={6} fontSize="sm">
              Including "do nothing" here is not a sales technique — it is the honest acknowledgment that for many Australian households in newer areas with modern plumbing, concern is low and no action is required. The point is to make an informed choice, not to act out of anxiety.
            </Text>
            <VStack spacing={4} align="stretch">
              {[
                {
                  action: "Do nothing — with awareness",
                  detail: "For households in newer properties, newer infrastructure areas, without known local contamination concerns, and without specific health vulnerabilities: Australian tap water is unlikely to pose meaningful risk at current exposure levels. This is a legitimate choice, made with eyes open.",
                  level: "No action",
                  levelColor: "green",
                },
                {
                  action: "Check your area's published water quality data",
                  detail: "State utility annual reports contain zone-level data for key parameters. Review figures for your zone and compare against ADWG guidelines. Note which contaminants are not reported (PFAS, microplastics) and what this absence means.",
                  level: "Information",
                  levelColor: "blue",
                },
                {
                  action: "Identify your property's plumbing age",
                  detail: "If your home was built before 1989, internal plumbing may contain lead solder or lead service connections. This risk is property-specific and invisible to utility reporting. It warrants independent tap testing regardless of your area's supply quality.",
                  level: "Due diligence",
                  levelColor: "orange",
                },
                {
                  action: "Independent lab test at your tap (UNSW)",
                  detail: "A comprehensive test of what is actually reaching your kitchen tap — not the supply zone average. Tests for parameters not included in utility reports, including PFAS, heavy metals, and specific DBP compounds. The most reliable way to know your actual exposure.",
                  level: "Highest certainty",
                  levelColor: "purple",
                },
                {
                  action: "Point-of-use activated carbon filtration",
                  detail: "Removes chlorine, chloramines, THMs, HAAs, and most other DBPs effectively. Does not remove fluoride, dissolved solids, heavy metals, or PFAS. The minimum reasonable intervention for households concerned about DBP exposure — particularly for drinking and cooking water.",
                  level: "Targeted intervention",
                  levelColor: "blue",
                },
                {
                  action: "Reverse osmosis (under-sink)",
                  detail: "Removes the broadest range of contaminants including dissolved solids, heavy metals, fluoride, nitrates, and many pharmaceutical compounds. The most comprehensive point-of-use option. Produces wastewater (2–4L per 1L treated). Should be preceded by sediment and carbon pre-filtration.",
                  level: "Comprehensive",
                  levelColor: "blue",
                },
                {
                  action: "Whole-home filtration",
                  detail: "Addresses chlorine, DBPs, sediment, and taste throughout the entire property — every tap and shower. Does not remove dissolved solids, fluoride, or PFAS. Appropriate for households with skin sensitivities, appliance protection concerns, or where whole-property treatment is preferred.",
                  level: "Whole property",
                  levelColor: "blue",
                },
              ].map((item) => (
                <Box
                  key={item.action}
                  p={5}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="xl"
                >
                  <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                    <Text fontWeight="700" color={NAVY} fontSize="sm">{item.action}</Text>
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      px={3} py={1}
                      borderRadius="full"
                      bg={ICE_MIST}
                      color={DEEP_AQUA}
                    >
                      {item.level}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.detail}</Text>
                </Box>
              ))}
            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Closing position statement */}
          <Box p={6} bg={ICE_MIST} borderRadius="xl" border={`1px solid ${WATER_BLUE}40`}>
            <HStack spacing={3} mb={4} align="flex-start">
              <Icon as={FaQuoteLeft} color={DEEP_AQUA} boxSize={5} mt={0.5} flexShrink={0} />
              <VStack align="flex-start" spacing={3}>
                <Text color="gray.700" lineHeight="tall" fontStyle="italic">
                  "Australian tap water is among the better-regulated in the world. The concerns outlined above are real but unevenly distributed — risk varies significantly by location, property age, proximity to contamination sources, and individual vulnerability. For most Australian households in modern properties, the immediate risk from tap water is low. For others, it warrants investigation.
                </Text>
                <Text color="gray.700" lineHeight="tall" fontStyle="italic">
                  The honest position is that we don't have complete information about the long-term effects of routine exposure to the full mixture of substances present in treated Australian tap water — because that research is difficult to conduct and has not been adequately funded. Regulatory compliance is not the same as a clean bill of health. It is the same as meeting the current standard. Those are different things."
                </Text>
                <Text fontSize="sm" fontWeight="600" color={NAVY}>— One Water Australia</Text>
              </VStack>
            </HStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Sources */}
          <Box>
            <Heading size="sm" color={NAVY} mb={4}>Sources & References</Heading>
            <VStack align="flex-start" spacing={2}>
              {[
                "Australian Drinking Water Guidelines (ADWG), NHMRC & NRMMC 2011 (updated 2022) — note: guidelines, not legally enforceable standards",
                "World Health Organization — Guidelines for Drinking-water Quality (4th Ed.)",
                "IARC Monographs Vol. 135 — PFOA (classified Group 1 carcinogen, 2023)",
                "IARC Monographs Vol. 52 — Chloroform (classified Group 2A probable carcinogen)",
                "NTP Monograph — Fluoride Exposure and Neurodevelopmental and Cognitive Health Effects (US National Toxicology Program, 2024)",
                "Green et al. — Association Between Maternal Fluoride Exposure During Pregnancy and IQ Scores in Offspring in Canada, JAMA Pediatrics (2020)",
                "Bashash et al. — Prenatal Fluoride Exposure and Cognitive Outcomes in Children, Environmental Health Perspectives (2017)",
                "Grandjean P & Landrigan PJ — Neurobehavioural Effects of Developmental Toxicity, Lancet Neurology (2014)",
                "Villanueva CM et al. — Bladder Cancer and Exposure to Water Disinfection By-Products Through Ingestion, Showering, Bathing and Swimming, Epidemiology (2007)",
                "Richardson SD et al. — Occurrence, Genotoxicity, and Carcinogenicity of Regulated and Emerging DBPs in Drinking Water, Mutation Research (2007)",
                "Leslie HA et al. — Discovery and Quantification of Plastic Particle Pollution in Human Blood, Environment International (2022)",
                "WHO — Microplastics in Drinking Water (2022)",
                "ATSDR — Toxicological Profile for Perfluoroalkyls (2021)",
                "Senate Standing Committee on Community Affairs — PFAS Contamination in Australia (2018)",
                "Department of Defence PFAS Investigation Program — Site Assessment Reports (Williamtown, Oakey, Tindal)",
                "Seqwater Annual Drinking Water Quality Report 2025–26",
                "Sydney Water Annual Drinking Water Quality Report 2024–25",
                "Edwards M et al. — Elevated Blood Lead in Young Children Due to Lead-Contaminated Drinking Water, Environmental Science & Technology (2009 — DC chloramine/lead event)",
                "Hanna-Attisha M et al. — Elevated Blood Lead Levels in Children Associated With the Flint Drinking Water Crisis, AJPH (2016)",
              ].map((s) => (
                <Text key={s} fontSize="xs" color="gray.500" lineHeight="tall">{s}</Text>
              ))}
            </VStack>
          </Box>

          {/* Final CTA */}
          <Box bg={NAVY} p={10} borderRadius="2xl" textAlign="center">
            <VStack spacing={5}>
              <Icon as={FaTint} boxSize={10} color={WATER_BLUE} />
              <Heading size="lg" color="white">Understanding your specific water is the starting point.</Heading>
              <Text color="whiteAlpha.800" maxW="lg">
                General assurances about Australian water quality tell you about the average. They don't tell you about your tap, your suburb, your plumbing, or your supply zone. An independent water assessment does.
              </Text>
              <Button
                as={RouterLink}
                to="/contact"
                bg={WATER_BLUE}
                color={NAVY}
                size="lg"
                fontWeight="bold"
                _hover={{ bg: "#8DC5E0" }}
                transition="all 0.2s"
              >
                Book a Free Water Assessment
              </Button>
            </VStack>
          </Box>

          {/* Internal links */}
          <Box>
            <Heading size="sm" color={NAVY} mb={4}>Explore Further</Heading>
            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
              {[
                { title: "Water Filters Hub", href: "/water/water-filters" },
                { title: "Water Filtration Systems", href: "/water/water-filtration" },
                { title: "Water Purifiers", href: "/water/water-purifiers" },
                { title: "Under-Sink Water Filter", href: "/water/water-filters/under-sink-system" },
                { title: "Under-Sink Water Purifier", href: "/water/water-purifiers/under-sink-system" },
                { title: "UV Filtration System", href: "/water/water-filtration/uv-system" },
                { title: "Chlorine in Tap Water", href: "/water/blog/chlorine-in-tap-water" },
                { title: "Hard Water Australia", href: "/water/blog/hard-water-australia" },
                { title: "Reverse Osmosis Guide", href: "/water/blog/reverse-osmosis-guide" },
                { title: "RO vs Whole-Home", href: "/water/blog/reverse-osmosis-vs-whole-home" },
                { title: "Gold Coast Water Quality", href: "/water/water-quality/gold-coast" },
                { title: "Seqwater 2026 Analysis", href: "/water/blog/seqwater-annual-report-2026" },
              ].map((l) => (
                <Box
                  key={l.title}
                  as={RouterLink}
                  to={l.href}
                  p={3}
                  bg="gray.50"
                  borderRadius="lg"
                  display="block"
                  textDecoration="none"
                  _hover={{ bg: ICE_MIST }}
                  transition="all 0.2s"
                >
                  <Text fontSize="xs" color={NAVY} fontWeight="500">{l.title}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterBlogTapWaterSafe;
